/**
 * Offline-Warteschlange (PWA-03): Änderungen an Zeiteinträgen werden ohne Verbindung in IndexedDB
 * gespeichert und bei Verbindung der Reihe nach an den Server übertragen.
 */

export interface QueuedRequest {
  id?: number;
  method: string;
  /** Pfad inkl. Query, relativ zur Origin */
  path: string;
  body?: string;
  createdAt: number;
  /** Vorläufige ID eines offline angelegten Eintrags */
  offlineId?: string;
}

export interface SyncProblem {
  path: string;
  status: number;
  detail: string;
}

const DB_NAME = 'firetracker-offline';
const STORE = 'queue';
let memoryFallback: QueuedRequest[] | null = null;
let memoryId = 0;
const listeners = new Set<(count: number) => void>();

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no-indexeddb'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexeddb'));
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = fn(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexeddb'));
  });
}

export async function enqueue(item: QueuedRequest): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.add(item));
  } catch {
    memoryFallback ??= [];
    memoryFallback.push({ ...item, id: ++memoryId });
  }
  await notify();
}

export async function list(): Promise<QueuedRequest[]> {
  try {
    const items = await withStore<QueuedRequest[]>('readonly', (s) => s.getAll() as IDBRequest<QueuedRequest[]>);
    return [...items, ...(memoryFallback ?? [])].sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [...(memoryFallback ?? [])];
  }
}

async function remove(id: number) {
  if (memoryFallback?.some((i) => i.id === id)) {
    memoryFallback = memoryFallback.filter((i) => i.id !== id);
    return;
  }
  try {
    await withStore('readwrite', (s) => s.delete(id));
  } catch {
    // ignorieren
  }
}

async function notify() {
  const count = (await list()).length;
  listeners.forEach((l) => l(count));
}

export function subscribe(listener: (count: number) => void) {
  listeners.add(listener);
  void list().then((items) => listener(items.length));
  return () => {
    listeners.delete(listener);
  };
}

function readCookie(name: string) {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')[1];
}

let flushing: Promise<SyncProblem[]> | null = null;

/**
 * Überträgt die Warteschlange. Vorläufige IDs werden durch die echten ersetzt; abgelehnte Änderungen
 * (4xx, z. B. Konflikt „nur ein laufender Timer“) werden verworfen und als Problem gemeldet.
 */
export function flush(): Promise<SyncProblem[]> {
  flushing ??= doFlush().finally(() => {
    flushing = null;
  });
  return flushing;
}

async function doFlush(): Promise<SyncProblem[]> {
  const problems: SyncProblem[] = [];
  const idMap = new Map<string, string | null>();
  for (const item of await list()) {
    let path = item.path;
    let skip = false;
    for (const [offlineId, realId] of idMap) {
      if (path.includes(offlineId)) {
        if (realId) path = path.replace(offlineId, realId);
        else skip = true;
      }
    }
    if (path.includes('/offline-') && !skip) {
      // Bezug auf einen Eintrag, der nicht mehr in der Warteschlange ist
      skip = true;
    }
    if (skip) {
      await remove(item.id!);
      continue;
    }
    let response: Response;
    try {
      const token = readCookie('XSRF-TOKEN');
      response = await globalThis.fetch(path, {
        method: item.method,
        credentials: 'same-origin',
        headers: {
          ...(item.body ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {}),
        },
        body: item.body,
      });
    } catch {
      break; // weiterhin offline
    }
    const retryLater =
      response.status >= 500 ||
      response.status === 401 ||
      (response.status === 403 && !response.headers.get('content-type')?.includes('problem'));
    if (retryLater) {
      break; // später erneut versuchen (Serverfehler, abgelaufene Sitzung, CSRF)
    }
    if (!response.ok) {
      const problem = (await response.json().catch(() => ({}))) as { detail?: string };
      problems.push({ path, status: response.status, detail: problem.detail ?? `HTTP ${response.status}` });
      if (item.offlineId) idMap.set(item.offlineId, null);
    } else if (item.offlineId) {
      const created = (await response.json().catch(() => ({}))) as { id?: string };
      idMap.set(item.offlineId, created.id ?? null);
    }
    await remove(item.id!);
  }
  await notify();
  return problems;
}

/** Nur einfache Zeiteintrags-Änderungen werden offline angenommen. */
export function isQueueable(method: string, pathname: string) {
  if (method === 'POST') return /\/api\/v1\/workspaces\/[^/]+\/time-entries(\/stop)?$/.test(pathname);
  if (method === 'PATCH' || method === 'DELETE') return /\/api\/v1\/workspaces\/[^/]+\/time-entries\/[^/]+$/.test(pathname);
  return false;
}

/**
 * Nimmt eine Anfrage in die Warteschlange und liefert eine vorläufige Antwort, damit die Oberfläche
 * weiterarbeiten kann. Start-/Endzeiten werden jetzt festgelegt, nicht erst bei der Übertragung.
 */
export async function queueAndRespond(request: Request): Promise<Response> {
  const url = new URL(request.url);
  const path = url.pathname + url.search;
  const now = new Date().toISOString();
  let body: Record<string, unknown> = {};
  if (request.method !== 'DELETE') {
    body = ((await request.json().catch(() => ({}))) ?? {}) as Record<string, unknown>;
  }
  const json = (data: unknown, status = 200) =>
    new Response(JSON.stringify(data), { status, headers: { 'Content-Type': 'application/json', 'X-Offline': 'queued' } });

  if (request.method === 'POST' && url.pathname.endsWith('/stop')) {
    body.end ??= now;
    await enqueue({ method: 'POST', path, body: JSON.stringify(body), createdAt: Date.now() });
    return json({ id: 'offline-stopped', end: body.end, tagIds: [], tags: [] });
  }
  if (request.method === 'POST') {
    const offlineId = `offline-${crypto.randomUUID()}`;
    body.start ??= now;
    await enqueue({ method: 'POST', path, body: JSON.stringify(body), createdAt: Date.now(), offlineId });
    const start = String(body.start);
    const end = (body.end as string | undefined) ?? undefined;
    return json(
      {
        ...body,
        id: offlineId,
        start,
        end,
        tagIds: (body.tagIds as string[] | undefined) ?? [],
        tags: [],
        billable: !!body.billable,
        durationSeconds: end ? Math.round((Date.parse(end) - Date.parse(start)) / 1000) : undefined,
        type: body.type ?? 'REGULAR',
        source: 'MOBILE',
        locked: false,
        invoiced: false,
        approvalStatus: 'NONE',
        version: 0,
      },
      201,
    );
  }
  await enqueue({
    method: request.method,
    path,
    body: request.method === 'DELETE' ? undefined : JSON.stringify(body),
    createdAt: Date.now(),
  });
  return request.method === 'DELETE' ? new Response(null, { status: 204, headers: { 'X-Offline': 'queued' } }) : json({ ...body });
}
